<template>
    <div v-if="plan"
         :class="['ob-plan-card', { 'active-plan': isActive && stepperMode, selected: isActive && !stepperMode, 'is-clickable': stepperMode }]"
         @click="onCardClick">
        <v-icon v-if="stepperMode" size="18" :class="['ob-plan-chevron', { open: expanded }]">
            mdi-chevron-down
        </v-icon>

        <div class="ob-plan-top">
            <div class="ob-plan-icon">
                <v-img :src="planImg" max-width="22" max-height="22" contain/>
            </div>
            <div>
                <div class="ob-plan-name">{{ plan.name }}</div>
                <div v-if="isActive && !stepperMode" class="ob-plan-badge">
                    {{ $t('common.hired') }}
                </div>
            </div>
        </div>

        <div class="ob-plan-price">
            {{ planAmount }} <span class="ob-plan-currency">{{ plan.currency }}</span>
        </div>
        <div class="ob-plan-period">{{ periodLabel }}</div>

        <v-expand-transition>
            <div v-show="showFeats" class="ob-plan-feats-wrap">
                <div class="ob-plan-includes">{{ $t('common.include') }}</div>
                <div class="ob-plan-feats">
                    <div class="ob-plan-feat" v-for="(feature, i) in features" :key="i">
                        <v-icon size="14" color="secondary">mdi-check</v-icon>
                        <span>{{ featureLabel(feature) }}</span>
                    </div>
                </div>
            </div>
        </v-expand-transition>

        <button v-if="!isActive || stepperMode"
                :class="['ob-plan-cta', { 'ob-plan-cta--active': isActive }]"
                @click.stop="contractPlan">
            {{ ctaLabel }}
        </button>
    </div>
</template>

<script>

export default {
    name: "SubscriptionCard",
    props: {
        plan: {
            type: Object,
            default: null
        },
        isActive: {
            type: Boolean,
            default: false,
        },
        discount: {
            type: Number,
            default: null
        },
        amountOff: {
            type: Number,
            default: null
        },
        stepperMode: {
            type: Boolean,
            default: false,
        },
        savings: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            expanded: false,
        }
    },
    computed: {
        planImg(vm = this) {
            return vm.plan?.img || vm.imgPlaceholder
        },
        features(vm = this) {
            return vm.plan?.features || []
        },
        showFeats(vm = this) {
            return !vm.stepperMode || vm.expanded
        },
        periodLabel(vm = this) {
            const base = vm.plan.intervalType === 'annual' ? vm.$t('common.per_year') : vm.$t('common.per_month')
            if (vm.savings && vm.savings > 0) {
                return `${base} · ${vm.$t('common.annual_savings', { currency: vm.plan.currency, amount: vm.savings })}`
            }
            return base
        },
        ctaLabel(vm = this) {
            if (vm.isActive) return vm.stepperMode ? vm.$t('common.selected') : vm.$t('common.hired')
            return vm.$t('common.contract')
        },
        esFeatureMap(vm = this) {
            const card = vm.$i18n.messages?.es?.plan_features?.card || {}
            return Object.keys(card).reduce((map, key) => {
                map[card[key]] = key
                return map
            }, {})
        },
        planAmount(vm = this) {
            if (vm.discount) return Number(vm.plan.amount - ((vm.plan.amount * vm.discount) / 100)).toFixed(1)
            if (vm.amountOff) return Math.max(0, vm.plan.amount - vm.amountOff)
            return vm.plan.amount
        },
    },
    methods: {
        onCardClick() {
            const vm = this
            if (vm.stepperMode) vm.expanded = !vm.expanded
        },
        contractPlan() {
            const vm = this
            vm.expanded = true
            vm.$emit('plan', vm.plan)
        },
        featureLabel(feature) {
            const vm = this
            const raw = String(feature)
            const num = raw.match(/^(\d+)\s+(.+)$/)
            if (num) {
                const word = num[2].trim().toLowerCase()
                if (word.indexOf('entrenador') === 0) return vm.$t('plan_features.card.coaches', { n: num[1] })
                if (word.indexOf('cliente') === 0) return vm.$t('plan_features.card.clients', { n: num[1] })
            }
            const upTo = raw.match(/^hasta\s+([\d.]+)\s+(.+)$/i)
            if (upTo) {
                const n = upTo[1]
                const word = upTo[2].trim().toLowerCase()
                if (word.indexOf('entrenador') === 0) return vm.$t('plan_features.card.up_to_coaches', { n })
                if (word.indexOf('cliente') === 0) return vm.$t('plan_features.card.up_to_clients', { n })
            }
            const key = vm.esFeatureMap[raw]
            return key ? vm.$t('plan_features.card.' + key) : raw
        }
    }
}
</script>

<style scoped>

.ob-plan-card {
    position: relative;
    display: flex;
    flex-direction: column;
    background: var(--v-surface-base);
    border: 1.5px solid var(--v-outline-base);
    border-radius: 12px;
    padding: 16px 14px;
    transition: border-color .15s ease, background .15s ease;
}

.ob-plan-card.is-clickable {
    cursor: pointer;
}

.ob-plan-card:hover {
    border-color: var(--v-secondary-base);
}

.ob-plan-card.selected {
    border-color: var(--v-secondary-base);
    background: rgba(102, 204, 204, 0.06);
}

.ob-plan-card.active-plan {
    background: #0F1A2A;
    border-color: rgba(102, 204, 204, 0.45);
    box-shadow: 0 0 0 4px rgba(102, 204, 204, 0.12);
}

.ob-plan-card.active-plan .ob-plan-icon {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.1);
}

.ob-plan-card.active-plan .ob-plan-name,
.ob-plan-card.active-plan .ob-plan-price {
    color: var(--v-secondary-base);
}

.ob-plan-card.active-plan .ob-plan-chevron {
    color: var(--v-secondary-base) !important;
}

.ob-plan-card.active-plan .ob-plan-feat {
    color: rgba(255, 255, 255, 0.6);
}

.ob-plan-chevron {
    position: absolute;
    top: 16px;
    right: 14px;
    color: var(--v-txtMuted-base) !important;
    transition: transform .2s ease;
}

.ob-plan-chevron.open {
    transform: rotate(180deg);
}

.ob-plan-top {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 14px;
}

.ob-plan-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    flex-shrink: 0;
    background: var(--v-field-base);
    border: 1px solid var(--v-outline-base);
    display: flex;
    align-items: center;
    justify-content: center;
}

.ob-plan-name {
    font-size: 12px;
    font-weight: 800;
    color: var(--v-accent-base);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    line-height: 1.2;
}

.ob-plan-badge {
    display: inline-block;
    margin-top: 4px;
    background: rgba(102, 204, 204, 0.14);
    border-radius: 5px;
    padding: 2px 8px;
    font-size: 10px;
    font-weight: 600;
    color: var(--v-secondary-base);
}

.ob-plan-price {
    font-size: 26px;
    font-weight: 800;
    line-height: 1;
    margin-bottom: 2px;
}

.ob-plan-currency {
    font-size: 14px;
    font-weight: 700;
}

.ob-plan-period {
    font-size: 9px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: 0.07em;
    margin-bottom: 10px;
}

.ob-plan-includes {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    margin: 4px 0 8px;
}

.ob-plan-feats {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.ob-plan-feat {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: 12px;
    color: var(--v-txtSecondary-base);
    line-height: 1.3;
}

.ob-plan-cta {
    margin-top: 14px;
    width: 100%;
    height: 38px;
    border-radius: 8px;
    border: 1px solid var(--v-outline-base);
    background: transparent;
    color: var(--v-txtSecondary-base);
    font-size: 13px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    transition: all .15s ease;
}

.ob-plan-cta:hover {
    border-color: var(--v-secondary-base);
    color: var(--v-accent-base);
}

.ob-plan-cta--active {
    background: var(--v-secondary-base);
    border-color: var(--v-secondary-base);
    color: #fff;
}

</style>
